import { supabase } from './supabaseService';

export type MfaFactor = { id: string; friendly_name?: string; factor_type: string; status: string };
export type MfaState = { mode: 'ready' | 'enroll' | 'challenge'; email: string; factors: MfaFactor[] };

export async function loadMfaState(): Promise<MfaState> {
  const { data: identity, error: identityError } = await supabase.auth.getUser();
  if (identityError || !identity.user) throw new Error('Vuelve a iniciar sesión para comprobar tu seguridad.');
  const [profile, factors, assurance] = await Promise.all([
    supabase.from('users').select('is_active,role,user_roles(name)').eq('id', identity.user.id).single(),
    supabase.auth.mfa.listFactors(),
    supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
  ]);
  if (profile.error || !profile.data?.is_active || factors.error || assurance.error || !assurance.data.currentLevel) {
    throw new Error('No se ha podido comprobar el segundo factor. Inténtalo de nuevo.');
  }
  const relation = profile.data.user_roles;
  const role = (Array.isArray(relation) ? relation[0]?.name : (relation as { name?: string } | null)?.name) || profile.data.role;
  const verified = factors.data.all.filter(factor => factor.status === 'verified');
  const mode = verified.length
    ? (assurance.data.currentLevel === 'aal2' ? 'ready' : 'challenge')
    : (role === 'superadmin' ? 'enroll' : 'ready');
  return { mode, email: identity.user.email || '', factors: verified };
}

export async function enrollAuthenticator() {
  // Only discard our unfinished enrollments, never a verified authenticator.
  const factors = await supabase.auth.mfa.listFactors();
  if (factors.error) throw new Error('No se han podido consultar los autenticadores.');
  for (const factor of factors.data.all) {
    if (factor.status === 'unverified' && factor.friendly_name?.startsWith('CRM Cero Ideas')) {
      const result = await supabase.auth.mfa.unenroll({ factorId: factor.id });
      if (result.error) throw new Error('No se ha podido reiniciar la configuración.');
    }
  }
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp', issuer: 'Cero Ideas CRM', friendlyName: `CRM Cero Ideas ${new Date().toISOString()}`,
  });
  if (error) throw new Error('No se ha podido preparar el autenticador. Inténtalo de nuevo.');
  return { id: data.id, qr: data.totp.qr_code, secret: data.totp.secret };
}

export async function verifyAuthenticator(factorId: string, code: string) {
  if (!/^\d{6}$/.test(code)) throw new Error('Introduce los seis dígitos de tu aplicación autenticadora.');
  const { error } = await supabase.auth.mfa.challengeAndVerify({ factorId, code });
  if (error) throw new Error('El código no es válido o ha caducado. Prueba con el código actual.');
  const result = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (result.error || result.data.currentLevel !== 'aal2') throw new Error('No se ha podido confirmar la verificación.');
}
